import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, LayoutDashboard, LogOut, Settings, ChevronDown, MessageSquare, Columns3, FileText, FlaskConical, Loader2, MapPin, Languages, Check, Palette, Pencil, Sun, Moon, Monitor, ChartNoAxesColumn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { AuthSession } from '@/types/auth';
import type { BrandIdentityDto } from '@/types/brand';
import { ColorMode, getDashboardUrl, useColorMode, useLogoPath, useBrandName } from '@/components/theme-provider';
import { getHomeLink } from '@/lib/home-link';
import { markLeaving } from '@/lib/auth-client';
import { useBrandFilter } from '@/components/brand-filter/brand-filter-context';
import { BrandFilterPopover } from '@/components/brand-filter/brand-filter-popover';

/** Auto-unpause presets (minutes) offered when pausing at peak hours — mirrors the backend enum. */
const REACTIVATE_PRESETS = [30, 60, 120, 180] as const;

/** Appearance options, in menu order. `SYSTEM` follows the OS preference. */
const COLOR_MODES = [
    { mode: ColorMode.LIGHT, icon: Sun, labelKey: 'header.appearance.light' },
    { mode: ColorMode.DARK, icon: Moon, labelKey: 'header.appearance.dark' },
    { mode: ColorMode.SYSTEM, icon: Monitor, labelKey: 'header.appearance.system' },
] as const;

interface AppHeaderProps {
    session: AuthSession | null;
    onLogout: () => Promise<void>;
    hasOrdersCapability: boolean;
    /** The Results tab: brand owner sessions of a brand that takes orders (D18). */
    showResults: boolean;
    /** The tenant's WhatsApp channel is on: chats, templates, playground and the kill switch. */
    showWhatsappChrome: boolean;
    /** The Chats tab: the WhatsApp chrome, unless the brand hides its chats from this sucursal. */
    showInbox: boolean;
    /** A WhatsApp line is actually connected — templates and the playground need one. */
    hasWhatsappLine: boolean;
    brandName?: string;
    /** Read-only tenant identity from `GET /brands/current` (K6): the merchant's own mark. */
    brandIdentity?: BrandIdentityDto;
    phone?: string;
    aiAgentEnabled: boolean;
    aiAgentPaused: boolean;
    hasBrand: boolean;
    onToggleAiAgent: (
        paused: boolean,
        options?: { autoReplyMessage?: string; reactivateInMinutes?: number },
    ) => Promise<void>;
    pendingOrdersCount?: number;
    handoffCount?: number;
}

export function AppHeader({
    session,
    onLogout,
    hasOrdersCapability,
    showResults,
    showWhatsappChrome,
    showInbox,
    hasWhatsappLine,
    brandName,
    brandIdentity,
    phone,
    aiAgentEnabled,
    aiAgentPaused,
    hasBrand,
    onToggleAiAgent,
    pendingOrdersCount,
    handoffCount,
}: AppHeaderProps) {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const user = session?.user;
    const activeLanguage = i18n.resolvedLanguage ?? i18n.language;
    const { colorMode, setColorMode } = useColorMode();
    const dashboardUrl = getDashboardUrl();
    // "Ir al Dashboard" or, for a session the cashier opened, "Volver a Caja".
    const home = getHomeLink();
    const HomeIcon = home.kind === 'cashier' ? ArrowLeft : LayoutDashboard;
    const logoPath = useLogoPath();
    const tenantLabel = useBrandName();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [toggling, setToggling] = useState(false);
    // Pause-dialog fields (both optional): heads-up message + auto-unpause preset.
    const [autoReplyMessage, setAutoReplyMessage] = useState('');
    const [reactivateInMinutes, setReactivateInMinutes] = useState<number | undefined>(undefined);

    const scope = session?.scope;
    const { isAggregate, isSingleBrand } = useBrandFilter();
    // Line-less location-scoped sessions ride the shared master line — pausing
    // it would pause every sucursal, so the kill switch is owner-only. In aggregate
    // the switch pauses ONE brand's master line, so it needs the filter narrowed to
    // exactly that brand before it can name a target.
    //
    // `aiAgentEnabled` is the tenant's `channels.whatsapp.aiAgentEnabled` now (K5): there is
    // nothing to pause on a tenant whose WhatsApp AI is off, and nothing at all on one whose
    // WhatsApp channel is off.
    const showKillSwitch = isAggregate
        ? isSingleBrand && hasBrand && aiAgentEnabled
        : showWhatsappChrome && hasBrand && aiAgentEnabled && (scope?.hasOwnLine ?? true);
    const currentPath = location.pathname;
    /** Board and list are the same nav entry: the badge is noise while either is on screen. */
    const onOrdersSurface = currentPath === '/board' || currentPath === '/orders';

    const handleToggleClick = () => {
        if (!aiAgentPaused) {
            setShowConfirmDialog(true);
        } else {
            handleConfirmToggle(false);
        }
    };

    const handleConfirmToggle = async (paused: boolean) => {
        setToggling(true);
        setShowConfirmDialog(false);
        try {
            await onToggleAiAgent(
                paused,
                paused
                    ? { autoReplyMessage: autoReplyMessage.trim() || undefined, reactivateInMinutes }
                    : undefined,
            );
            // Reset the dialog fields once applied so the next pause starts blank.
            setAutoReplyMessage('');
            setReactivateInMinutes(undefined);
        } finally {
            setToggling(false);
        }
    };

    const handleLogout = async () => {
        // Cashier-opened session: end only the agent session, then back to the cashier. Never
        // hit the dashboard /logout — the counter's cashier login must survive.
        if (home.kind === 'cashier') {
            try {
                // Don't hang the counter on a slow /sso/logout: give it 3 s, then leave anyway.
                await Promise.race([onLogout(), new Promise((resolve) => setTimeout(resolve, 3000))]);
            } finally {
                markLeaving();
                window.location.replace(home.url);
            }
            return;
        }
        if (dashboardUrl) {
            window.location.href = `${dashboardUrl}/logout`;
        }
        await onLogout();
    };

    const navTab = (path: string, label: string, icon: React.ReactNode) => (
        <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(path)}
            aria-current={currentPath === path ? 'page' : undefined}
            className={`text-sm gap-1.5 px-2 sm:px-3 ${
                currentPath === path
                    ? 'text-foreground font-medium bg-muted'
                    : 'text-muted-foreground'
            }`}
        >
            {icon}
            <span className="hidden sm:inline">{label}</span>
        </Button>
    );

    return (
        <>
            <header className="h-14 bg-background border-b border-border flex items-center justify-between gap-2 px-2 sm:px-4 shrink-0">
                {/* Left: Logo + nav tabs */}
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <img src={logoPath} alt={tenantLabel} className="app-logo h-7 w-7 shrink-0" />
                    {/* Aggregate sessions span many brands, so the identity block becomes the
                        filter that decides which of them every list below is showing. */}
                    {isAggregate && <BrandFilterPopover />}
                    {/* The merchant's own brand: its logo and colour swatch, read-only, next to
                        its name. The app mark above stays the whitebrand host's — that one is the
                        product's, this one is the tenant's (§6.2, K6). Identity is edited once, in
                        the dashboard; the link says where. */}
                    {!isAggregate && brandIdentity?.logoUrl && (
                        <img
                            src={brandIdentity.logoUrl}
                            alt={brandName ?? ''}
                            className="hidden sm:block h-7 w-7 shrink-0 rounded-full border border-border bg-background object-contain"
                        />
                    )}
                    {!isAggregate && (brandName || phone) && (
                        <div className="hidden sm:flex flex-col min-w-0">
                            {brandName && (
                                <span className="flex items-center gap-1.5 min-w-0">
                                    <span className="text-sm font-semibold text-foreground truncate max-w-[160px] leading-tight">
                                        {brandName}
                                    </span>
                                    {[brandIdentity?.primaryColorHex, brandIdentity?.secondaryColorHex]
                                        .filter(Boolean)
                                        .map((hex) => (
                                            <Tooltip key={hex}>
                                                <TooltipTrigger asChild>
                                                    <span
                                                        aria-hidden="true"
                                                        className="h-2.5 w-2.5 shrink-0 rounded-full border border-border"
                                                        style={{ backgroundColor: hex as string }}
                                                    />
                                                </TooltipTrigger>
                                                <TooltipContent>{hex}</TooltipContent>
                                            </Tooltip>
                                        ))}
                                    {dashboardUrl && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <a
                                                    href={`${dashboardUrl}/commerce/identity`}
                                                    aria-label={t('header.editIdentity')}
                                                    className="text-muted-foreground hover:text-foreground shrink-0"
                                                >
                                                    <Pencil className="h-3 w-3" />
                                                </a>
                                            </TooltipTrigger>
                                            <TooltipContent>{t('header.editIdentity')}</TooltipContent>
                                        </Tooltip>
                                    )}
                                </span>
                            )}
                            {phone && (
                                <span className="text-[11px] text-muted-foreground truncate max-w-[160px] leading-tight">
                                    {phone}
                                </span>
                            )}
                        </div>
                    )}
                    {!isAggregate && scope?.locationId && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap">
                            <MapPin className="h-3 w-3" />
                            {t('header.viewing', {
                                name: scope.locationName ?? t('header.locationFallback'),
                            })}
                        </span>
                    )}
                    <Separator orientation="vertical" className="h-6 hidden sm:block" />
                    <nav className="flex items-center gap-0.5 sm:gap-1">
                        {/* The board has its own route ("/" is only the landing decision, which for a
                            manual-WhatsApp brand is the inbox); the orders list hangs off its view switcher. */}
                        {hasOrdersCapability && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/board')}
                                aria-current={onOrdersSurface ? 'page' : undefined}
                                className={`text-sm gap-1.5 px-2 sm:px-3 ${
                                    onOrdersSurface
                                        ? 'text-foreground font-medium bg-muted'
                                        : 'text-muted-foreground'
                                }`}
                            >
                                <Columns3 className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('header.nav.board')}</span>
                                {!!pendingOrdersCount && !onOrdersSurface && (
                                    <span aria-live="polite" className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-yellow-500 text-white text-[10px] font-bold leading-none">
                                        {pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}
                                    </span>
                                )}
                            </Button>
                        )}
                        {/* WhatsApp chrome: the inbox exists only for a tenant selling over
                            WhatsApp (and not for a sucursal its brand hides chats from);
                            templates and the playground additionally need a line. */}
                        {showInbox && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/conversations')}
                                aria-current={currentPath === '/conversations' ? 'page' : undefined}
                                className={`text-sm gap-1.5 px-2 sm:px-3 ${
                                    currentPath === '/conversations'
                                        ? 'text-foreground font-medium bg-muted'
                                        : 'text-muted-foreground'
                                }`}
                            >
                                <MessageSquare className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('header.nav.chats')}</span>
                                {!!handoffCount && (
                                    <span aria-live="polite" className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                                        {handoffCount > 99 ? '99+' : handoffCount}
                                    </span>
                                )}
                            </Button>
                        )}
                        {showWhatsappChrome &&
                            hasWhatsappLine &&
                            navTab('/templates', t('header.nav.templates'), <FileText className="h-4 w-4" />)}
                        {showWhatsappChrome &&
                            hasWhatsappLine &&
                            navTab('/playground', t('header.nav.playground'), <FlaskConical className="h-4 w-4" />)}
                        {showResults &&
                            navTab('/results', t('header.nav.results'), <ChartNoAxesColumn className="h-4 w-4" />)}
                        {/* Settings as a visible destination, not only an item in the gear menu:
                            merchants look for it in the nav. */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/settings')}
                            aria-current={currentPath.startsWith('/settings') ? 'page' : undefined}
                            className={`text-sm gap-1.5 px-2 sm:px-3 ${
                                currentPath.startsWith('/settings')
                                    ? 'text-foreground font-medium bg-muted'
                                    : 'text-muted-foreground'
                            }`}
                        >
                            <Settings className="h-4 w-4" />
                            <span className="hidden sm:inline">{t('header.settings')}</span>
                        </Button>
                    </nav>
                </div>

                {/* Center/Right: Kill switch + controls */}
                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                    {showKillSwitch && (
                        <>
                            <button
                                onClick={handleToggleClick}
                                disabled={toggling}
                                className={`flex items-center gap-2 px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                                    aiAgentPaused
                                        ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 animate-pulse'
                                        : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700'
                                } ${toggling ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`}
                            >
                                {toggling ? (
                                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                                ) : (
                                    <span
                                        className={`inline-block h-2 w-2 rounded-full ${
                                            aiAgentPaused ? 'bg-red-500' : 'bg-emerald-500'
                                        }`}
                                    />
                                )}
                                <span className="hidden min-[380px]:inline">
                                    {aiAgentPaused ? t('header.aiPaused') : t('header.aiActive')}
                                </span>
                            </button>
                            <Separator orientation="vertical" className="h-6 hidden sm:block" />
                        </>
                    )}

                    {home.url && (
                        <Button variant="ghost" size="sm" className="text-muted-foreground hidden sm:flex" asChild>
                            <a href={home.url}>
                                <HomeIcon className="h-4 w-4 mr-1.5" />
                                {t(home.labelKey)}
                            </a>
                        </Button>
                    )}

                    <DropdownMenu>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" aria-label={t('header.settings')} className="gap-1.5 px-2">
                                        <Settings className="h-5 w-5 text-muted-foreground" />
                                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                    </Button>
                                </DropdownMenuTrigger>
                            </TooltipTrigger>
                            <TooltipContent>{t('header.settings')}</TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent align="end" className="w-56">
                            {(user?.name || user?.email) && (
                                <>
                                    <DropdownMenuLabel className="font-normal">
                                        <div className="text-left text-sm leading-tight">
                                            {user.name && <span className="block truncate font-semibold">{user.name}</span>}
                                            {user.email && (
                                                <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
                                            )}
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                </>
                            )}
                            <DropdownMenuGroup>
                                <DropdownMenuItem onClick={() => navigate('/settings')}>
                                    <Settings />
                                    {t('header.settings')}
                                </DropdownMenuItem>
                            </DropdownMenuGroup>
                            <DropdownMenuSeparator />
                            <DropdownMenuGroup>
                                <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1.5">
                                    <Languages className="h-3.5 w-3.5" />
                                    {t('header.language.label')}
                                </DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => void i18n.changeLanguage('es')}>
                                    {t('header.language.spanish')}
                                    {activeLanguage === 'es' && <Check className="ml-auto h-4 w-4" />}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => void i18n.changeLanguage('en')}>
                                    {t('header.language.english')}
                                    {activeLanguage === 'en' && <Check className="ml-auto h-4 w-4" />}
                                </DropdownMenuItem>
                            </DropdownMenuGroup>
                            <DropdownMenuSeparator />
                            <DropdownMenuGroup>
                                <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1.5">
                                    <Palette className="h-3.5 w-3.5" />
                                    {t('header.appearance.label')}
                                </DropdownMenuLabel>
                                {COLOR_MODES.map(({ mode, icon: Icon, labelKey }) => (
                                    <DropdownMenuItem key={mode} onClick={() => setColorMode(mode)}>
                                        <Icon />
                                        {t(labelKey)}
                                        {colorMode === mode && <Check className="ml-auto h-4 w-4" />}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuGroup>
                            <DropdownMenuSeparator />
                            {home.url && (
                                <>
                                    <DropdownMenuGroup>
                                        <DropdownMenuItem asChild>
                                            <a href={home.url}>
                                                <HomeIcon />
                                                {t(home.labelKey)}
                                            </a>
                                        </DropdownMenuItem>
                                    </DropdownMenuGroup>
                                    <DropdownMenuSeparator />
                                </>
                            )}
                            <DropdownMenuItem onClick={handleLogout}>
                                <LogOut />
                                {t('header.logout')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            {/* Confirmation dialog for pausing AI agent */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('header.pauseDialog.title')}</DialogTitle>
                        <DialogDescription>{t('header.pauseDialog.description')}</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        {/* Optional heads-up sent to customers while paused. */}
                        <div className="space-y-1.5">
                            <Label htmlFor="pause-auto-reply">{t('header.pauseDialog.autoReplyLabel')}</Label>
                            <Textarea
                                id="pause-auto-reply"
                                value={autoReplyMessage}
                                onChange={(e) => setAutoReplyMessage(e.target.value)}
                                placeholder={t('header.pauseDialog.autoReplyPlaceholder')}
                                rows={3}
                                maxLength={1000}
                            />
                        </div>

                        {/* Optional auto-unpause. "Manual" = stays off until turned on. */}
                        <div className="space-y-1.5">
                            <Label>{t('header.pauseDialog.reactivateLabel')}</Label>
                            <div className="flex flex-wrap gap-2">
                                {REACTIVATE_PRESETS.map((minutes) => (
                                    <Button
                                        key={minutes}
                                        type="button"
                                        size="sm"
                                        variant={reactivateInMinutes === minutes ? 'default' : 'outline'}
                                        onClick={() => setReactivateInMinutes(minutes)}
                                    >
                                        {t(`header.pauseDialog.reactivatePresets.${minutes}`)}
                                    </Button>
                                ))}
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={reactivateInMinutes === undefined ? 'default' : 'outline'}
                                    onClick={() => setReactivateInMinutes(undefined)}
                                >
                                    {t('header.pauseDialog.reactivateManual')}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                            {t('header.pauseDialog.cancel')}
                        </Button>
                        <Button variant="destructive" onClick={() => handleConfirmToggle(true)}>
                            {t('header.pauseDialog.confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
